import { NgClass, NgStyle } from '@angular/common'
import { ChangeDetectorRef, Component, OnInit, ViewChild, computed, inject } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { faX } from '@fortawesome/free-solid-svg-icons'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import { ComboBox } from '@grapecity/wijmo.input'
import imageCompression, { Options } from 'browser-image-compression'

import { ListItem, MaterialTicket, MaterialTicketType, RepositoryModal } from '../../shared'
import { MaterialsComponent } from './materials.component'

@Component({
    //changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './material.component.html',
    standalone: true,
    imports: [FormsModule, WjInputModule, NgClass, FaIconComponent, NgStyle],
})
export class MaterialComponent extends RepositoryModal<MaterialTicket> implements OnInit {
    // Icons and UI Elements
    faX = faX
    @ViewChild('materialComboBox', { static: true }) private _materialComboBox!: ComboBox

    // Material Type and Configuration
    materialType!: MaterialTicketType
    materialTicketType = MaterialTicketType
    hasDumpFee = false

    // Dropdown Options - Material Types
    haulOffTypeOptions: ListItem<boolean>[] = [new ListItem(true, 'With Dump Fee'), new ListItem(false, 'No Dump Fee')]
    concreteTypeMap: ListItem<string>[] = [new ListItem('CONCRETE', 'Concrete'), new ListItem('FLOWFILL', 'Flowfill')]

    // Dropdown Options - Jobs and Vendors
    jobMap: ListItem<string>[] = []
    vendorOptions: ListItem<number>[] = []
    pumpTruckVendorOptions: ListItem<number>[] = []
    concreteTesterVendorOptions: ListItem<number>[] = []

    // Dropdown Options - Materials and Locations
    allPits: any[] = []
    allMaterials: any[] = []
    lotOptions: ListItem<string>[] = []
    materialOptions: ListItem<string>[] = []

    // Dropdown Options - Units and Equipment
    unitOptions: ListItem<string>[] = []
    truckOptions: ListItem<string>[] = []
    trailerOptions1: ListItem<string>[] = []
    trailerOptions2: ListItem<string>[] = []

    // File Upload and Progress
    images: any[] = []
    progress = 0

    // Computed Properties
    isLessor = computed(() => this.services.authentication.isLessor())

    // Injected Services
    private crd = inject(ChangeDetectorRef)

    ngOnInit() {
        this.unitOptions.push(new ListItem('Yards', 'Yards'))
        this.unitOptions.push(new ListItem('Tons', 'Tons'))
        if (this.materialType == MaterialTicketType.HaulOff) this.unitOptions.push(new ListItem('Loads', 'Loads'))

        this.materialType = this.editOptions.initializeParams.materialType
        if (this.isDuplicating) {
            this.target['id'] = undefined as any
            this.target.quantity = undefined
            this.target.ticket = undefined
            this.target.imageUrls = []
        }
        if (this.isAdding) {
            this.target.units = 'Tons'
            this.target.truck = this.editOptions.initializeParams.component.userTruck
            this.target.date = new Date()
            this.target.createdOn = new Date()
            if (this.materialType == MaterialTicketType.Concrete) {
                this.target.concreteType = 'CONCRETE'
                this.target.units = 'Yards'
            }
        }
        const parentComponent = this.editOptions.initializeParams.component as MaterialsComponent

        if (this.materialType == MaterialTicketType.SandGravel || this.materialType == MaterialTicketType.HaulOff) {
            this.vendorOptions = parentComponent.aggVendors.map(x => new ListItem(x.id, x.name))
        } else {
            this.vendorOptions = parentComponent.concreteVendors.map(x => new ListItem(x.id, x.name))
        }
        this.pumpTruckVendorOptions = parentComponent.pumpTruckVendors.map(x => new ListItem(x.id, x.name))
        this.concreteTesterVendorOptions = parentComponent.concreteTesterVendors.map(x => new ListItem(x.id, x.name))

        this.allPits = parentComponent.pits
        this.allMaterials = parentComponent.materials
        this.jobMap = this.services.data.vpJobs.items.map(x => new ListItem(x.id, x.id + ' ' + x.name))
        this.truckOptions = parentComponent.availableTrucks.map(x => new ListItem(x.equipment, x.description))
        this.trailerOptions1 = parentComponent.availableTrailers.map(x => new ListItem(x.equipment, x.description))
        this.trailerOptions2 = parentComponent.availableTrailers.map(x => new ListItem(x.equipment, x.description))

        // When loading a saved material for edit, we want to restore the chained combobox values.
        if (this.target.vendor) {
            const lot = this.target.lot
            this.vendorChanged()
            setTimeout(() => {
                this.target.lot = lot
                if (this.target.lot) {
                    const material = this.target.material
                    this.lotChanged()
                    setTimeout(() => {
                        this.target.material = material
                        this.crd.detectChanges() // Latestly, we need to update the change detection, since we are changing values through code.
                    }, 10)
                }
            }, 10)
        }
    }

    vendorChanged() {
        if (this.materialType == MaterialTicketType.Concrete) return
        this.lotOptions = []
        this.materialOptions = []
        const filteredPits = this.allPits.filter(x => x.vendor === this.target.vendor)
        this.lotOptions = filteredPits.map(x => new ListItem(x.addressSeq, x.description)) // TODO: The id may not be vendorGroup, but addressSeq.
        setTimeout(() => {
            this.target.lot = undefined
            this.crd.detectChanges() // Latestly, we need to update the change detection, since we are changing values through code.
        }, 1)
    }

    lotChanged() {
        this.materialOptions = []
        const filteredMaterials = this.allMaterials.filter(x => x.vendor === this.target.vendor && x.location === this.target.lot)
        this.materialOptions = filteredMaterials.map(x => new ListItem(x.material, x.description))
        setTimeout(() => {
            this.target.material = undefined
            this.crd.detectChanges() // Latestly, we need to update the change detection, since we are changing values through code.
        }, 1)
    }

    materialChanged() {
        //    var material = this.allMaterials.filter(x => x.vendor === this.target.vendor && x.location === this.target.lot && x.material == this.target.material)[0];
        //    if (material) {
        //        this.target.price = material.price;
        //    }
    }

    removeImage(image) {
        this.images.remove(image)
    }

    getPendingImageSrc(image) {
        if (image.type == 'application/pdf') {
            return './Resources/pdfImage.png'
        }
        return image.url
    }

    getExistingImageSrc(url: string) {
        if (url.indexOf('.pdf') > -1) {
            return './Resources/pdfImage.png'
        }
        return './Resources/Images/Material/' + url + '?w=300'
    }

    isPdf(url: string): boolean {
        return url.toLowerCase().endsWith('.pdf')
    }

    openImage(url) {
        window.open('./Resources/Images/Material/' + url, '_blank')
    }

    async onFileChanged(event) {
        const files = event.target.files

        const progress = (p: number) => {
            this.progress = p
        }

        for (const file of files) {
            if (file.type !== 'application/pdf') {
                const options: Options = {
                    maxSizeMB: 2,
                    maxWidthOrHeight: 1920,
                    useWebWorker: true,
                    onProgress: progress,
                }
                const compressedFile = await imageCompression(file, options)
                compressedFile['url'] = await imageCompression.getDataUrlFromFile(compressedFile)
                this.images.push(compressedFile)
            } else {
                this.images.push(file)
            }
        }
    }

    protected onSave(resolveSave: (result?: any) => void, rejectSave: (reason?: any) => void) {
        const observable = this.isAdding || this.isDuplicating ? this.repositoryAdd() : this.repositoryEdit()
        observable.subscribe({
            next: successResult => {
                if (this.images.length === 0) {
                    resolveSave(successResult)
                    return
                }

                //If the saving of the record is successfult, we will upload any images.
                const uploadData = new FormData()
                uploadData.append('id', successResult.id)
                for (let i = 0; i < this.images.length; i++) {
                    uploadData.append('myFile' + i, this.images[i], this.images[i].name)
                }
                this.services.data.post('api/MaterialTicket/UploadImages', uploadData).subscribe({
                    next: _uploadResult => {
                        resolveSave(successResult)
                    },
                    error: errorResult => {
                        rejectSave(errorResult)
                    },
                })
            },
            error: errorResult => {
                rejectSave(errorResult)
            },
        })
    }

    protected onBeforeSave() {
        if (this.target.date && this.target.date > new Date()) {
            this.services.notification.warning('You may not enter a future date.')
            return false
        }
        if (!this.target.date) this.target.date = new Date()
        this.target.materialType = this.materialType
        if ((this.isAdding || this.isDuplicating) && this.images.length === 0) {
            if (this.materialType != MaterialTicketType.HaulOff) {
                this.services.notification.warning('Please include the ticket image before saving.')
                return false
            } else if (this.hasDumpFee) {
                this.services.notification.warning('Please include the ticket image before saving.')
                return false
            }
        }
        const material = this.allMaterials.filter(
            x => x.vendor === this.target.vendor && x.location === this.target.lot && x.material == this.target.material,
        )[0]
        if (material) {
            this.target.price = material.price
        }
        if (!this.hasDumpFee && this.materialType == MaterialTicketType.HaulOff) {
            this.target.quantity = 1
            this.target.units = 'Loads'
        }
        return true
    }
}
